import {
  $computeTableMapSkipCellCheck,
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getTableNodeFromLexicalNodeOrThrow,
  $isTableCellNode,
  $isTableSelection,
} from '@lexical/table';
import {
  $createParagraphNode,
  $getSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_DOWN_COMMAND,
  LexicalEditor,
} from 'lexical';
import {useEffect} from 'react';

import {areAllCellsEmpty} from './utils';

/**
 * Handles Delete/Backspace to delete empty rows/columns or the entire table
 */
export function useTableDeleteHandler(editor: LexicalEditor) {
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        // Check if this is a delete or backspace key
        if (event.key !== 'Backspace' && event.key !== 'Delete') {
          return false;
        }

        return editor.getEditorState().read(() => {
          const selection = $getSelection();

          // Only handle table selections
          if (!$isTableSelection(selection)) {
            return false;
          }

          const nodes = selection.getNodes();
          const cells = nodes.filter($isTableCellNode);

          if (cells.length === 0) {
            return false;
          }

          // Check if all cells are empty
          if (!areAllCellsEmpty(cells)) {
            return false;
          }

          // Get the first cell to find the table
          const firstCell = cells[0];
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(firstCell);
          const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);

          // Determine if it's a full row or column selection
          const numRows = tableMap.length;
          const numColumns = tableMap[0]?.length || 0;

          // Collect row and column indices of selected cells
          const selectedRowIndices = new Set<number>();
          const selectedColumnIndices = new Set<number>();

          for (let r = 0; r < numRows; r++) {
            for (let c = 0; c < numColumns; c++) {
              const cellInfo = tableMap[r]?.[c];
              if (cellInfo?.cell) {
                const cellKey = cellInfo.cell.getKey();
                if (cells.some((cell) => cell.getKey() === cellKey)) {
                  selectedRowIndices.add(r);
                  selectedColumnIndices.add(c);
                }
              }
            }
          }

          const isFullRowSelection =
            selectedColumnIndices.size === numColumns && selectedRowIndices.size > 0;
          const isFullColumnSelection =
            selectedRowIndices.size === numRows && selectedColumnIndices.size > 0;

          // Check if ALL cells in the entire table are selected (100% selection)
          const isEntireTableSelected =
            selectedRowIndices.size === numRows &&
            selectedColumnIndices.size === numColumns &&
            cells.length === numRows * numColumns;

          // If entire table is selected and empty, delete the table
          if (isEntireTableSelected) {
            event.preventDefault();
            editor.update(() => {
              // Insert a paragraph node after the table to place the cursor
              const newParagraph = $createParagraphNode();
              tableNode.insertAfter(newParagraph);

              // Remove the table
              tableNode.remove();

              // Clear the selection and set cursor to the new paragraph
              newParagraph.select();
            });
            return true;
          }

          // Otherwise, delete just the selected rows or columns
          if (isFullRowSelection || isFullColumnSelection) {
            // Delete the rows/columns
            event.preventDefault();
            editor.update(() => {
              if (isFullRowSelection) {
                $deleteTableRowAtSelection();
              } else {
                $deleteTableColumnAtSelection();
              }
            });
            return true;
          }

          return false;
        });
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);
}
